import KODIAK_FARM_FACTORY_ABI from 'abis/KodiakFarmFactory.json'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { DeployFarmTransactionInfo, TransactionType } from 'state/transactions/types'
import { isAddress } from 'utils'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi } from 'viem'
import { decodeEventLog } from 'viem'
import { Address, zeroAddress } from 'viem'

import { KODIAKFARM_FACTORY_ADDRESSES, KODIAKFARM_IMPLEMENTATION_ADDRESSES } from '../../../constants/addresses'

interface FarmDeploymentArgs {
  stakingToken: string
  rewardTokens: string[]
  rewardManagers: string[]
  rewardRates: bigint[]
}

interface DeploymentResult {
  isDeploying: boolean
  farmAddress: string | null
  error: string | null
  deploy: (args: FarmDeploymentArgs) => Promise<string | undefined>
}

export function useFarmDeployment(): DeploymentResult {
  const { account, chainId, wagmiPublicClient } = useConnection()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const [isDeploying, setIsDeploying] = useState(false)
  const [farmAddress, setFarmAddress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const addTransaction = useTransactionAdder()
  const { writeContractAsync } = useWriteContract()

  const farmFactoryAddress = chainId ? KODIAKFARM_FACTORY_ADDRESSES[chainId] : undefined
  const implementationAddress = chainId ? KODIAKFARM_IMPLEMENTATION_ADDRESSES[chainId] : undefined

  const validateInputs = (args: FarmDeploymentArgs): string | null => {
    if (!isAddress(args.stakingToken)) {
      return 'Invalid staking token address'
    }

    if (args.stakingToken === zeroAddress) {
      return 'Invalid staking token address'
    }

    if (args.rewardTokens.length === 0) {
      return 'At least one reward token required'
    }

    if (args.rewardTokens.length !== args.rewardManagers.length) {
      return 'Reward tokens and managers count mismatch'
    }

    for (let i = 0; i < args.rewardTokens.length; i++) {
      if (!isAddress(args.rewardTokens[i])) {
        return `Invalid reward token address at position ${i}`
      }
      if (!isAddress(args.rewardManagers[i])) {
        return `Invalid reward manager address at position ${i}`
      }
    }

    return null
  }

  const deploy = async (args: FarmDeploymentArgs) => {
    if (!account) {
      setError('Wallet not connected')
      return undefined
    }

    if (!farmFactoryAddress || !implementationAddress || !wagmiPublicClient) {
      setError('Farm contracts are undefined')
      return undefined
    }

    const validationError = validateInputs(args)
    if (validationError) {
      setError(validationError)
      return undefined
    }

    setIsDeploying(true)
    setError(null)

    try {
      const tx = {
        address: farmFactoryAddress,
        abi: KODIAK_FARM_FACTORY_ABI as Abi,
        functionName: 'deployFarm',
        args: [
          implementationAddress,
          args.stakingToken as Address,
          args.rewardTokens as Address[],
          args.rewardManagers as Address[],
          args.rewardRates,
          '0x',
        ],
        account,
      }

      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      // Create correct transaction info object
      const transactionInfo: DeployFarmTransactionInfo = {
        type: TransactionType.FARM_DEPLOYMENT,
        stakingToken: args.stakingToken,
        rewardTokens: args.rewardTokens,
        rewardManagers: args.rewardManagers,
        rewardRates: args.rewardRates.map((rate) => rate.toString()),
      }

      addTransaction({ hash: txHash }, transactionInfo)

      const receipt = await waitForTransactionReceipt({ hash: txHash })

      // Find the FarmDeployed event in the receipt logs
      const deploymentLog = receipt.logs.find((log) => {
        try {
          const decoded = decodeEventLog({
            abi: KODIAK_FARM_FACTORY_ABI as Abi,
            data: log.data,
            topics: log.topics,
          })
          return decoded.eventName === 'FarmDeployed'
        } catch {
          return false
        }
      })

      if (deploymentLog) {
        const decoded = decodeEventLog({
          abi: KODIAK_FARM_FACTORY_ABI as Abi,
          data: deploymentLog.data,
          topics: deploymentLog.topics,
        })
        if (decoded.eventName === 'FarmDeployed' && decoded.args) {
          const args = decoded.args as unknown as { farm: Address; implementation: Address }
          setFarmAddress(args.farm)
        }
      }

      return txHash
    } catch (err: any) {
      console.error('Failed to deploy farm:', err)
      setError(err?.message || 'Failed to deploy farm')
    } finally {
      setIsDeploying(false)
    }
    return undefined
  }

  return {
    isDeploying,
    farmAddress,
    error,
    deploy,
  }
}
